import moment from "moment";
import GuestService from "@/services/guestService";

export const GuestStore = {
    namespaced: true,
    state: {
        tabs: {
            active: {
                guest_id: null,
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 20,
                currentPage: 1,
                sortField: "last_name",
                sortOrder: "-1",
                first: 0,
            },
            inactive: {
                guest_id: null,
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 20,
                currentPage: 1,
                sortField: "last_name",
                sortOrder: "-1",
                first: 0,
            },
        },
        currentTab: "active",
        searchText: "",
        fromDate: null,
        toDate: null,
        isLoading: false,
        lastSelectedGuest: "",
    },

    mutations: {
        SET_TABLE_DATA(state, { tab, data }) {
            state.tabs[tab].tableData = data.data || [];
            state.tabs[tab].totalRecords = data.count || 0;
        },
        UPDATE_CURRENT_PAGE(state, { tab, page }) {
            state.tabs[tab].currentPage = page;
        },
        UPDATE_ROWS_PER_PAGE(state, { tab, rows }) {
            state.tabs[tab].rowsPerPage = rows;
        },
        UPDATE_SORT_OPTIONS(state, { tab, field, order }) {
            state.tabs[tab].sortField = field;
            state.tabs[tab].sortOrder = order;
        },
        RESET_FIRST_VALUE(state, { tab }) {
            const currentPage = state.tabs[tab].currentPage;
            const rowsPerPage = state.tabs[tab].rowsPerPage;
            state.tabs[tab].first = currentPage > 0 ? (currentPage - 1) * rowsPerPage : 0;
        },
        RESET_CURRENT_PAGE(state, { tab, page = 0 }) {
            state.tabs[tab].currentPage = page;
        },
        UPDATE_SEARCH_TEXT(state, searchText) {
            state.searchText = searchText;
        },
        UPDATE_CURRENT_TAB(state, currentTab) {
            state.currentTab = currentTab;
        },
        SET_LOADING(state, isLoading) {
            state.isLoading = isLoading;
        },
        UPDATE_FROM_DATE(state, fromDate) {
            state.fromDate = fromDate;
        },
        UPDATE_TO_DATE(state, toDate) {
            state.toDate = toDate;
        },
        SET_LAST_SELECTED_GUEST(state, lastGuestId) {
            state.lastSelectedGuest = lastGuestId;
        },
    },

    actions: {
        async fetchTableData({ commit, state, dispatch }) {
            commit("SET_LOADING", true);
            const currentTabState = state.tabs[state.currentTab];
            const params = buildParams(currentTabState, state);
            const queryString = buildQueryString(params);

            try {
                const data = await GuestService.getGuests(`?${queryString}`);
                commit("SET_TABLE_DATA", { tab: state.currentTab, data });
            } catch (error) {
                console.error("Error fetching guests:", error);
                commit("SET_TABLE_DATA", { tab: state.currentTab, data: [] });
            } finally {
                commit("SET_LOADING", false);
                dispatch("resetFirstValue");
            }
        },

        updateTableSettings({ commit, dispatch, state }, { rows, page }) {
            commit("UPDATE_ROWS_PER_PAGE", { tab: state.currentTab, rows });
            commit("UPDATE_CURRENT_PAGE", { tab: state.currentTab, page: page + 1 });
            dispatch("fetchTableData");
        },

        updateSortOptions({ commit, dispatch, state }, { field, order }) {
            commit("UPDATE_SORT_OPTIONS", { tab: state.currentTab, field, order });
            dispatch("fetchTableData");
        },

        updateSearchCriteria({ commit, dispatch }, { searchText, fromDate, toDate }) {
            commit("UPDATE_SEARCH_TEXT", searchText);
            commit("UPDATE_FROM_DATE", fromDate);
            commit("UPDATE_TO_DATE", toDate);
            dispatch("fetchTableData");
        },

        updateTab({ commit, dispatch }, currentTab) {
            commit("UPDATE_CURRENT_TAB", currentTab);
            dispatch("fetchTableData");
        },

        resetFirstValue({ commit, state }) {
            commit("RESET_FIRST_VALUE", { tab: state.currentTab });
        },

        resetCurrentPage({ commit, state }, page = 0) {
            commit("RESET_CURRENT_PAGE", { tab: state.currentTab, page });
        },
    },
};

// ---------- Helper Functions ----------

function buildQueryString(params) {
    return Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");
}

function buildParams(currentTabState, state) {
    let params = {
        page: currentTabState.currentPage,
        limit: currentTabState.rowsPerPage,
        orderBy: currentTabState.sortField,
        order: currentTabState.sortOrder,
        tab: state.currentTab,
    };

    if (state.searchText) {
        params.searchTerm = state.searchText;
    }

    if (state.fromDate) {
        params.fromDate = moment(state.fromDate).format("DD/MM/YYYY");
    }

    if (state.toDate) {
        params.toDate = moment(state.toDate).format("DD/MM/YYYY");
    }

    switch (state.currentTab) {
        case "active":
            params.is_active = true;
            break;
        case "inactive":
            params.is_active = false;
            break;
        default:
            break;
    }

    return params;
}